import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Slot rendered before the label (e.g. an icon). */
  leadingIcon?: ReactNode;
  /** Slot rendered after the label. */
  trailingIcon?: ReactNode;
  /** Forces the button to occupy the full width of its container. */
  fullWidth?: boolean;
  /** Disables the button and shows a loading indicator. */
  loading?: boolean;
}

/**
 * Branded button. Variants map to the brand spec's content hierarchy:
 * - `primary`   — Foundation Green CTA, used for the most important action
 *                 on a page (e.g. "Sign in").
 * - `secondary` — Outlined; secondary actions.
 * - `ghost`     — No chrome; toolbar / inline actions.
 * - `danger`    — Fuel Orange; destructive actions.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    fullWidth,
    loading,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={[
        styles.button,
        styles[`variant_${variant}`],
        styles[`size_${size}`],
        fullWidth ? styles.fullWidth : '',
        loading ? styles.loading : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {!loading && leadingIcon && <span className={styles.icon}>{leadingIcon}</span>}
      <span className={styles.label}>{children}</span>
      {!loading && trailingIcon && <span className={styles.icon}>{trailingIcon}</span>}
    </button>
  );
});
